import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Chalkboard,
  Users,
  Briefcase,
  CheckCircle,
  ArrowRight,
  Buildings,
  GraduationCap,
  Handshake,
  ChartLineUp,
  Target,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const packages = [
  {
    title: "Basic Package",
    description: "Short practical training sessions for staff development, entrepreneurship, customer care, productivity improvement, and workplace effectiveness.",
    price: "Starting from $200 / session",
    features: [
      "Half-day or full-day sessions",
      "Up to 30 participants",
      "Training materials included",
      "Certificate of completion",
    ],
    popular: false,
  },
  {
    title: "Advanced Package",
    description: "Comprehensive organizational training covering leadership, team performance, financial literacy, innovation, and enterprise growth strategies.",
    price: "Starting from $500 / program",
    features: [
      "Multi-day training programs",
      "Up to 50 participants",
      "Pre and post assessments",
      "Follow-up support",
      "Customized curriculum",
    ],
    popular: true,
  },
  {
    title: "Enterprise Package",
    description: "Tailored long-term training partnerships for corporates, NGOs, government agencies, and institutions seeking measurable transformation.",
    price: "Custom quotation",
    features: [
      "Long-term partnership",
      "Unlimited participants",
      "Full needs assessment",
      "Ongoing mentorship",
      "Impact measurement",
      "Executive coaching",
    ],
    popular: false,
  },
];

const trainingAreas = [
  "Leadership Development",
  "Workforce Productivity",
  "Entrepreneurship Skills",
  "Customer Service Excellence",
  "Financial Literacy",
  "Business Management",
  "Innovation & Technology Adoption",
  "Team Building & Communication",
];

const clientTypes = [
  { icon: Buildings, title: "Private Companies & SMEs", description: "Enhance employee skills and business performance" },
  { icon: Handshake, title: "NGOs & Development Organizations", description: "Strengthen program delivery and team capacity" },
  { icon: Target, title: "Government Agencies", description: "Improve public service delivery and efficiency" },
  { icon: GraduationCap, title: "Schools & TVET Institutions", description: "Train educators and administrators" },
  { icon: Users, title: "Community Organizations", description: "Build leadership and organizational capacity" },
];

export default function B2BTrainingPage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-green-950" data-testid="b2b-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-dots" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              B2B Training
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Customized Training Solutions for Organizations
            </h1>
            <p className="text-lg text-green-100/80 leading-relaxed mb-8">
              PEDA offers business-to-business training programs designed to improve workforce productivity, strengthen leadership, and enhance operational performance for companies, NGOs, schools, and public institutions.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="b2b-request-quote-button"
            >
              <Link to="/contact">
                Request Training Proposal <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 md:py-28" data-testid="b2b-packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Training Packages
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Choose Your Training Package
            </h2>
            <p className="text-lg text-slate-600">
              Flexible training solutions tailored to your organization's needs and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-3xl p-8 ${
                  pkg.popular 
                    ? "bg-gradient-to-br from-green-700 to-green-800 text-white border-2 border-amber-400" 
                    : "bg-white border border-slate-200 shadow-sm"
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-amber-500 text-green-950 text-xs font-bold rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className={`text-2xl font-bold mb-3 ${pkg.popular ? "text-white" : "text-slate-900"}`}>
                  {pkg.title}
                </h3>
                <p className={`text-sm leading-relaxed mb-4 ${pkg.popular ? "text-green-100" : "text-slate-600"}`}>
                  {pkg.description}
                </p>
                <p className={`text-xl font-bold mb-6 ${pkg.popular ? "text-amber-400" : "text-green-700"}`}>
                  {pkg.price}
                </p>
                
                <div className="space-y-3 mb-8">
                  {pkg.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <CheckCircle 
                        className={`h-5 w-5 shrink-0 ${pkg.popular ? "text-amber-400" : "text-green-700"}`} 
                        weight="fill" 
                      />
                      <span className={`text-sm ${pkg.popular ? "text-green-100" : "text-slate-700"}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  asChild 
                  className={`w-full rounded-xl ${
                    pkg.popular 
                      ? "bg-amber-500 hover:bg-amber-600 text-green-950" 
                      : "bg-green-700 hover:bg-green-800 text-white"
                  }`}
                  data-testid={`b2b-package-${index}-button`}
                >
                  <Link to="/contact">Get Quotation</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Areas */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="b2b-training-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                What We Offer
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Training Areas
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our expert trainers deliver practical, results-oriented programs across a wide range of business and professional development areas.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {trainingAreas.map((area) => (
                  <div key={area} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                    <CheckCircle className="h-5 w-5 text-green-700 shrink-0" weight="fill" />
                    <span className="font-medium text-slate-700">{area}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-700 to-green-800 rounded-3xl p-8 text-white">
              <Chalkboard className="h-12 w-12 text-amber-400 mb-6" weight="duotone" />
              <h3 className="text-2xl font-bold mb-4">Custom Training Programs</h3>
              <p className="text-green-100 leading-relaxed mb-6">
                We understand that every organization has unique training needs. Our team works closely with you to design customized programs that address your specific challenges and goals.
              </p>
              <div className="space-y-3">
                {[
                  "Needs assessment and gap analysis",
                  "Customized curriculum development",
                  "Flexible delivery formats",
                  "Post-training support and follow-up",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-amber-400 shrink-0" weight="fill" />
                    <span className="text-green-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 md:py-28" data-testid="b2b-clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Clients
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Who We Serve
            </h2>
            <p className="text-lg text-slate-600">
              Our B2B training solutions are ideal for organizations of all sizes across various sectors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientTypes.map((client, index) => (
              <motion.div
                key={client.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <client.icon className="h-6 w-6 text-green-700" weight="duotone" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{client.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{client.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-amber-500" data-testid="b2b-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-green-950 mb-6">
            Ready to Empower Your Team?
          </h2>
          <p className="text-lg text-green-900/80 mb-8 leading-relaxed">
            Contact us today to discuss your training needs and receive a customized proposal for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-green-700 hover:bg-green-800 text-white rounded-xl px-8"
              data-testid="b2b-contact-button"
            >
              <Link to="/contact">
                Request Training Proposal <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="bg-white border-green-700 text-green-700 hover:bg-green-50 rounded-xl px-8"
              data-testid="b2b-services-button"
            >
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
